import { IBaseTheme } from "@interfaces/sourcing.interface";

/**
 * Finds the deepest selected theme in the same branch as the given theme.
 *
 * @param theme - The theme from the tree (e.g., level 2) that is being unselected.
 * @param selectedThemes - The current list of selected themes (from form state).
 * @returns The deepest selected theme in that branch, or null if none found.
 */
export const findDeepestSelectedThemeInBranch = (
  theme: IBaseTheme,
  selectedThemes: IBaseTheme[]
): IBaseTheme | null => {
  // Get all IDs in the branch path of the given theme
  const branchIds = theme.parent_list.map((p) => p.id);

  // Find all selected themes whose parent_list starts with branchIds
  const candidates = selectedThemes.filter((t) => {
    const tIds = t.parent_list.map((p) => p.id);
    // Check if tIds starts with branchIds
    if (tIds.length < branchIds.length) return false;
    return branchIds.every((id, i) => id === tIds[i]);
  });

  if (!candidates.length) return null;

  // Return the theme with the longest parent_list (deepest in the tree)
  return candidates.reduce((deepest, current) =>
    current.parent_list.length > deepest.parent_list.length ? current : deepest
  );
};

/**
 * Whether a selected theme other than the one being removed still lives under `parentId`.
 *
 * The backend collapses a parent tag onto its whole subtree, so re-tagging the parent while a
 * sibling remains selected under it would drop that sibling. Callers use this to decide whether
 * the parent should be re-tagged when a theme is removed.
 *
 * @param parentId - The id of the parent theme being considered for re-tagging.
 * @param removedThemeId - The id of the theme currently being removed (excluded from the check).
 * @param selectedThemes - The current list of selected themes (from form state).
 */
export const hasSelectedSiblingUnderParent = (
  parentId: string,
  removedThemeId: string,
  selectedThemes: IBaseTheme[]
): boolean =>
  selectedThemes.some(
    (theme) =>
      theme.id !== removedThemeId && (theme.parent_list?.some((p) => p.id === parentId) ?? false)
  );

/**
 * Collapses a theme list so that, per branch, only the deepest node is kept: drops any theme that
 * is an ancestor of another theme in the same list. The backend stores only leaf themes, so a list
 * should never carry both a parent (e.g. level 1) and its descendant (level 2 nested under it) —
 * merging a company's themes into the deal's can otherwise produce exactly that pair.
 *
 * A theme is an ancestor of another when that other theme's `parent_list` contains its id.
 */
export const collapseToDeepestThemes = <T extends IBaseTheme>(themes: T[]): T[] =>
  themes.filter(
    (theme) =>
      !themes.some(
        (other) =>
          other.id !== theme.id && (other.parent_list?.some((p) => p.id === theme.id) ?? false)
      )
  );
